/*
 * Issue #227: labels and headline mapping for the captured scenario.
 *
 * The calculator templates carry no <label for> elements and no data-label attributes -- an input's
 * visible label is a sibling <p> and a result's is whatever text happens to sit above it. Walking the
 * DOM for those works for inputs (the .panel__item structure is consistent) but is fragile for
 * results, whose markup differs per calculator. So result labels come from this map, keyed on the real
 * data-result attribute, with a DOM lookup only as a fallback.
 *
 * The strings are the visible result phrasing from the production templates. They appear in the
 * results email tables and in the Surefire notes block, so keep them human-readable.
 */

export const RESULT_LABELS = {
    // Mortgage Payment
    monthlyPayments: 'Monthly Payment',
    principal: 'Total Principal',
    printerest: 'Total Interest',

    // Purchasing Power
    totalLoan: 'Maximum Home Price',
    DownPayment: 'Down Payment',
    LoanAmount: 'Loan Amount',

    // Qualifying Loan
    consLA: 'Conservative Loan Amount',
    aggLA: 'Aggressive Loan Amount',

    // Rent vs. Buy
    savings: 'Total Savings',
    savingspend: 'Rent + Tax Savings',
    sellHome: 'Gain from Home Sale',
    expectedYrs: 'Expected Years in Home',

    // Refinance
    difference: 'Monthly Savings',
    newMonthlyPayment: 'New Monthly Payment (P&I)',
    cashAvailable: 'Cash Available',
    additionalCashRemaining: 'Additional Cash Remaining',
    ACR: 'Additional Cash Remaining',

    // 15 vs. 30 Year
    totPayST: 'Short Term Total Payments',
    totPayLT: 'Long Term Total Payments',
    expyrs: 'Expected Years in Home',

    // Buydown
    totalbuydownamount: 'Total Buydown Fee',
    bdThirdParty: 'Third Party Pays',
    bdYou: 'You Pay',
    // Hidden capture spans feeding AC-4's payment-by-year chart. Only the years the selected
    // structure has are populated; the rest stay blank and are dropped from the tables.
    bdYear1Payment: 'Year 1 Monthly Payment',
    bdYear2Payment: 'Year 2 Monthly Payment',
    bdYear3Payment: 'Year 3 Monthly Payment',
    bdYear4Payment: 'Year 4 Monthly Payment',
    bdYear5Payment: 'Year 5 Monthly Payment',

    // Bi-Weekly
    biWeeklyPayments: 'Bi-Weekly Payment',
    biweeklyAmt: 'Annual Paid (Bi-Weekly)',
    monthlyAmt: 'Annual Paid (Monthly)',
    interestSaved: 'Interest Saved',
    // Hidden capture spans feeding AC-4's total-interest comparison chart.
    totalInterestMonthly: 'Total Interest (Monthly Plan)',
    totalInterestBiweekly: 'Total Interest (Bi-Weekly Plan)',
    acceleratedPayoff: 'Payoff In',
    differenceIAmts: 'Annual Difference'
};

/*
 * Result keys that hold an injected HTML block rather than a single figure. Refinance and 15-vs-30
 * render their whole headline into these, so they are read as the headline text and excluded from the
 * results table -- otherwise the table would contain one row with a paragraph in it.
 */
export const HTML_BLOCK_RESULT_KEYS = ['basicTitle', 'advancedTitle'];

/*
 * Which result carries the headline figure per calculator, and how to label it. Taken from the
 * mockup's per-calculator email previews.
 *
 * prefix/suffix decorate the captured figure for display ("$2,275.44/mo"). They are needed because
 * most templates render the currency symbol in an <i> element OUTSIDE the [data-result] span, so the
 * captured text is a bare "2,275.44". buildHeadline applies them guardedly -- a value that already
 * starts with "$" (buydown, qualifying) is left alone.
 */
export const HEADLINES = {
    'mortgage-payment': { key: 'monthlyPayments', label: 'Estimated Monthly Payment', prefix: '$', suffix: '/mo' },
    'purchasing-power': { key: 'totalLoan', label: 'Your new home can cost up to', prefix: '$' },
    'qualifying-loan': { key: 'consLA', secondKey: 'aggLA', label: 'Your loan can range between', join: ' – ', prefix: '$' },
    'rent-vs-buy': { key: 'savings', label: 'Your total savings', prefix: '$' },
    // Per the mockup's refinance email: a discrete headline figure with a cash-out sub-line, not the
    // injected basicTitle sentence (which rendered a whole paragraph at headline size). The pane
    // exposes difference/newMonthlyPayment as hidden capture spans that savings.js populates on each
    // calculate. subKey/subTemplate feed buildSubLabel in capture.js.
    'refinance': {
        key: 'difference',
        label: 'Estimated Monthly Savings',
        prefix: '$',
        subKey: 'cashAvailable',
        subTemplate: 'Plus {value} cash available at closing'
    },
    '15-vs-30-year-mortgage': { key: 'totPayST', secondKey: 'totPayLT', label: 'Total payments, short vs. long term', join: ' vs. ', prefix: '$' },
    'buy-down': { key: 'totalbuydownamount', label: 'Total buydown fee for this loan', prefix: '$' },
    'biweekly-payment': { key: 'biWeeklyPayments', label: 'Bi-Weekly Payment', prefix: '$' }
};

export function resultLabel(key, domFallback) {
    if (RESULT_LABELS[key]) return RESULT_LABELS[key];
    if (domFallback) return domFallback;
    return key;
}
